import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play, Repeat, Shuffle, X } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { hoverLabel } from "@/lib/format";
import { pageClass, reducedMotion, themeVars } from "@/lib/themes";
import { ThemeBackdrop } from "./ThemeBackdrop";
import { Particles } from "./Particles";

const PARTICLE = { snow: "snow", bats: "bats", confetti: "confetti", fireworks: "fireworks", petals: "petals", hearts: "hearts" };
const CLASS = { slide: "slide", pastel: "pastel" };
const SPEEDS = [3, 5, 8];

const shuffled = (n) => {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i -= 1) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
};

const Slide = ({ it, cls, kb, speed, onEnded }) => (
  <div className={`ss-slide ${cls}`} data-testid="slideshow-slide">
    {it.media_type === "chapter" ? (
      <div className="ss-chapter" data-testid={`slideshow-chapter-${it.year}`}><span className="pc-deco" aria-hidden="true" /><div className={`yb-year${it.title ? " yb-year-title" : ""}`}>{it.title || it.year}</div><div className="yb-count">{it.count} {it.count === 1 ? "photo" : "photos"}</div></div>
    ) : it.media_type === "video" ? (
      <video src={mediaUrl(it.video_url)} poster={mediaUrl(it.full_url)} muted autoPlay playsInline onEnded={onEnded} className="ss-media" data-testid="slideshow-video" />
    ) : (
      <img src={mediaUrl(it.full_url)} alt="" className={`ss-media ${kb ? `ss-kb ss-kb-${kb}` : ""}`} style={{ animationDuration: `${speed + 1.5}s` }} />
    )}
  </div>
);

export const Slideshow = ({ items, hasMore, onLoadMore, theme, startIndex = 0, onClose }) => {
  const still = reducedMotion();
  const transition = theme?.transition || "fade";
  const [order, setOrder] = useState(() => items.map((_, i) => i));
  const [pos, setPos] = useState(() => Math.max(0, startIndex));
  const [prev, setPrev] = useState(null);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(5);
  const [loop, setLoop] = useState(true);
  const [shuffle, setShuffle] = useState(false);
  const [chrome, setChrome] = useState(true);
  const touch = useRef(null);
  const hideT = useRef(null);
  const root = useRef(null);

  useEffect(() => { setOrder((o) => (items.length > o.length ? [...o, ...Array.from({ length: items.length - o.length }, (_, k) => o.length + k)] : o)); }, [items.length]);
  const cur = items[order[pos]];

  const go = useCallback((d) => {
    setPos((p) => {
      let n = p + d;
      if (n >= order.length) { if (hasMore) return p; if (!loop) { setPlaying(false); return p; } n = 0; }
      if (n < 0) n = loop ? order.length - 1 : 0;
      if (n !== p) setPrev(p);
      return n;
    });
  }, [order.length, hasMore, loop]);

  useEffect(() => { if (hasMore && pos >= order.length - 4) onLoadMore?.(); }, [pos, order.length, hasMore, onLoadMore]);
  useEffect(() => {
    if (!playing || !cur || cur.media_type === "video") return undefined;
    const t = setTimeout(() => go(1), speed * 1000);
    return () => clearTimeout(t);
  }, [playing, pos, speed, cur, go]);
  useEffect(() => {
    if (prev === null) return undefined;
    const t = setTimeout(() => setPrev(null), still ? 0 : 1200);
    return () => clearTimeout(t);
  }, [prev, still]);
  useEffect(() => {
    [1, 2].forEach((k) => { const n = items[order[(pos + k) % Math.max(order.length, 1)]]; if (n && n.full_url && n.media_type === "photo") new Image().src = mediaUrl(n.full_url); });
  }, [pos, order, items]);

  const poke = useCallback(() => {
    setChrome(true);
    clearTimeout(hideT.current);
    hideT.current = setTimeout(() => setChrome(false), 2500);
  }, []);
  useEffect(() => {
    poke();
    root.current?.requestFullscreen?.().catch(() => {});
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
      poke();
    };
    window.addEventListener("keydown", onKey);
    const onFs = () => { if (!document.fullscreenElement && root.current?.dataset.fs === "1") onClose(); };
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFs);
      clearTimeout(hideT.current);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, [go, onClose, poke]);

  const toggleShuffle = () => {
    const on = !shuffle;
    setShuffle(on);
    const curIdx = order[pos];
    const next = on ? shuffled(items.length).filter((i) => i !== curIdx) : items.map((_, i) => i).filter((i) => i !== curIdx);
    setOrder([curIdx, ...next]);
    setPos(0);
    setPrev(null);
  };
  const cls = useMemo(() => (still ? "ss-still" : `ss-in-${CLASS[transition] || "fade"}`), [still, transition]);
  if (!cur) return null;
  const caption = cur.media_type === "chapter" ? null : hoverLabel(cur);

  return (
    <div ref={root} className={`slideshow ${pageClass(theme)} ${chrome ? "chrome" : ""}`} style={themeVars(theme)} onMouseMove={poke} data-fs="1"
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; poke(); }}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); }}
      data-testid="slideshow" data-transition={transition} data-reduced-motion={still ? "1" : "0"}>
      <ThemeBackdrop theme={theme} fixed={false} />
      <div className="ss-dim" />
      {prev !== null && items[order[prev]] && <Slide key={`p${prev}`} it={items[order[prev]]} cls={`ss-out-${CLASS[transition] || "fade"}`} speed={speed} />}
      <Slide key={`c${pos}-${order[pos]}`} it={cur} cls={cls} kb={still ? null : (pos % 2) + 1} speed={speed} onEnded={() => playing && go(1)} />
      {!still && PARTICLE[transition] && cur.media_type !== "video" && <Particles type={PARTICLE[transition]} burstKey={pos} />}
      {caption && <div className="ss-caption" key={`cap${pos}`} data-testid="slideshow-caption">{caption}</div>}
      <div className="ss-controls" data-testid="slideshow-controls">
        <button onClick={() => go(-1)} aria-label="Previous" data-testid="slideshow-prev"><ChevronLeft size={20} /></button>
        <button onClick={() => setPlaying((p) => !p)} aria-label={playing ? "Pause" : "Play"} data-testid="slideshow-play-toggle">{playing ? <Pause size={18} /> : <Play size={18} />}</button>
        <button onClick={() => go(1)} aria-label="Next" data-testid="slideshow-next"><ChevronRight size={20} /></button>
        <span className="ss-sep" />
        {SPEEDS.map((s) => <button key={s} className={speed === s ? "on" : ""} onClick={() => setSpeed(s)} data-testid={`slideshow-speed-${s}`}>{s}s</button>)}
        <span className="ss-sep" />
        <button className={loop ? "on" : ""} onClick={() => setLoop((l) => !l)} aria-label="Loop" data-testid="slideshow-loop"><Repeat size={16} /></button>
        <button className={shuffle ? "on" : ""} onClick={toggleShuffle} aria-label="Shuffle" data-testid="slideshow-shuffle"><Shuffle size={16} /></button>
        <span className="ss-count" data-testid="slideshow-counter">{pos + 1} / {order.length}{hasMore ? "+" : ""}</span>
        <button onClick={onClose} aria-label="Exit slideshow" data-testid="slideshow-exit"><X size={18} /></button>
      </div>
    </div>
  );
};
